import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@aspnet/signalr';
import { environment } from '@app/../environments/environment';

/**
 * SignalR service cho notification hub.
 * Endpoint: /api/notificationhub
 * Event realtime: NEW_NOTIFICATION_COMMING (giữ nguyên typo theo docs backend).
 *
 * Khi nhận được event sẽ emit qua `notificationReceived$` để các subscriber
 * (NotificationStateService / component chuông) tự reload danh sách + bell.
 */
@Injectable()
export class SignalRService implements OnDestroy {
  private hubConnection: HubConnection | null = null;
  private connectionState = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelayMs = 5000;

  /** Emit payload từ server khi có event NEW_NOTIFICATION_COMMING */
  public notificationReceived$ = new Subject<any>();
  /** Emit true/false khi kết nối thay đổi */
  public connectionStateChanged$ = new Subject<boolean>();

  constructor(private zone: NgZone) {}

  /**
   * Khởi tạo kết nối tới /api/notificationhub.
   * Truyền JWT (Bearer) qua query string `access_token` theo chuẩn SignalR ASP.NET Core.
   */
  start(): void {
    if (this.hubConnection && this.connectionState) {
      return;
    }

    const token = localStorage.getItem(environment.authTokenKey);
    const hubUrl = `${environment.backendhost}/api/notificationhub`;

    const builder = new HubConnectionBuilder();
    this.hubConnection = builder
      .withUrl(hubUrl, {
        accessTokenFactory: () => (token ? token : ''),
      })
      .configureLogging(LogLevel.Information)
      .build();

    this.registerHandlers();

    this.hubConnection
      .start()
      .then(() => {
        this.connectionState = true;
        this.reconnectAttempts = 0;
        this.connectionStateChanged$.next(true);
        console.log('[SignalR] Connected to /api/notificationhub');
      })
      .catch((err) => {
        this.connectionState = false;
        this.connectionStateChanged$.next(false);
        console.error('[SignalR] Connection error:', err);
        this.scheduleReconnect();
      });
  }

  private registerHandlers(): void {
    if (!this.hubConnection) return;

    // Event name giữ nguyên typo theo docs backend (mục 3.1)
    this.hubConnection.on('NEW_NOTIFICATION_COMMING', (payload: any) => {
      // SignalR callback chạy ngoài Angular zone → cần zone.run để CD kích hoạt
      this.zone.run(() => {
        this.notificationReceived$.next(payload);
      });
    });

    this.hubConnection.onclose((error?: any) => {
      this.connectionState = false;
      this.connectionStateChanged$.next(false);
      console.warn('[SignalR] Connection closed', error);
      this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[SignalR] Max reconnect attempts reached. Stop retrying.');
      return;
    }
    this.reconnectAttempts++;
    setTimeout(() => {
      // Chỉ reconnect nếu còn token (còn đăng nhập)
      const token = localStorage.getItem(environment.authTokenKey);
      if (token && token.length > 10) {
        this.start();
      }
    }, this.reconnectDelayMs * this.reconnectAttempts);
  }

  stop(): void {
    if (this.hubConnection) {
      this.hubConnection.stop().then(() => {
        this.connectionState = false;
        this.connectionStateChanged$.next(false);
        console.log('[SignalR] Stopped');
      });
      this.hubConnection = null;
    }
  }

  isConnected(): boolean {
    return this.connectionState;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
